import { addCalendarDays, addCalendarMonths, formatJobNumber } from '@fieldops/shared'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { addressLine, money } from '../../lib/format.js'
import { useJobHref } from '../../lib/job-overlay.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { MARK_LABEL, markOf, SlotDot, SlotKey, type Mark } from '../repeat/ServiceTracks.js'
import { Button, Card, EmptyState, ErrorState, SkeletonRows } from '../ui.js'

export type CalendarVisit = RouterOutputs['agreements']['calendar']['visits'][number]

export type CalendarFilters = {
  status: 'ALL' | CalendarVisit['agreement']['status']
  agreementId: string | null
  service: string | null
  term: string
}

/** A day shows this many visits before the rest fold into "+n more". */
const PER_DAY = 3

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** Where the visit is: the site's name, else its building's client, else the address. */
export function visitSite(visit: CalendarVisit): string {
  const site = visit.site
  if (!site) return '—'
  const owner = site.client.id !== visit.client.id ? site.client.name : null
  return site.name ?? owner ?? addressLine(site.address)
}

export function visitMatches(visit: CalendarVisit, filters: CalendarFilters): boolean {
  const term = filters.term.trim().toLowerCase()
  return (
    (filters.status === 'ALL' || visit.agreement.status === filters.status) &&
    (filters.agreementId === null || visit.agreement.id === filters.agreementId) &&
    (filters.service === null || visit.services.includes(filters.service)) &&
    (term === '' ||
      [visit.agreement.name, visit.client.name, visitSite(visit), addressLine(visit.site?.address), visit.site?.client.name ?? '']
        .some((text) => text.toLowerCase().includes(term)))
  )
}

const markOfVisit = (visit: CalendarVisit): Mark => markOf(visit.slot, null, visit.paused)

/** Monday is 0. */
const weekday = (day: string) => (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7

/** Every day the month's grid draws: Monday on or before the 1st to Sunday on or after the last. */
export function monthGrid(month: string): string[] {
  const first = `${month}-01`
  const last = addCalendarDays(addCalendarMonths(first, 1), -1)
  const end = addCalendarDays(last, 6 - weekday(last))
  const days: string[] = []
  for (let day = addCalendarDays(first, -weekday(first)); day <= end; day = addCalendarDays(day, 1)) days.push(day)
  return days
}

const monthTitle = (month: string) =>
  new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric' }).format(new Date(`${month}-15T12:00:00`))

const longDay = (day: string) =>
  new Intl.DateTimeFormat('en-AU', { weekday: 'long', day: 'numeric', month: 'long' }).format(
    new Date(`${day}T12:00:00`),
  )

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

function visitTitle(visit: CalendarVisit): string {
  return [
    visit.services.join(', ') || 'No service',
    visitSite(visit),
    visit.client.name,
    `AG-${visit.agreement.number} ${visit.agreement.name}`,
    MARK_LABEL[markOfVisit(visit)],
    visit.slot.tentative ? 'date may move' : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** A visit with a job opens the job; one not made yet opens its agreement. */
function VisitLink({ visit, className, children }: { visit: CalendarVisit; className?: string; children: ReactNode }) {
  const jobHref = useJobHref()
  return (
    <Link
      to={visit.job ? jobHref(visit.job.id) : `/agreements/${visit.agreement.id}`}
      title={visitTitle(visit)}
      aria-label={visitTitle(visit)}
      className={className}
    >
      {children}
    </Link>
  )
}

/**
 * Every agreement's visits on a month, for seeing when the work falls. The
 * days are the server's — a visit's booking when its job has one, its due
 * day otherwise — so this and the agreement page cannot disagree. Rule 50.
 *
 * A desk draws the month as a grid and lists a picked day under it; the
 * field draws the month as a list of days.
 */
export function AgreementCalendar({
  month,
  today,
  filters,
  onMonth,
}: {
  month: string
  /** The device's today, until the server's arrives. */
  today: string
  filters: CalendarFilters
  onMonth: (month: string) => void
}) {
  const trpc = useTRPC()
  const days = monthGrid(month)
  const { data, isPending, error, refetch, isPlaceholderData } = useQuery({
    ...trpc.agreements.calendar.queryOptions({ from: days[0]!, to: days.at(-1)! }),
    placeholderData: keepPreviousData,
  })
  const [picked, setPicked] = useState<string | null>(null)
  const selected = picked && days.includes(picked) ? picked : null
  const now = data?.today ?? today

  const shown = (data?.visits ?? []).filter((visit) => visitMatches(visit, filters))
  const byDay = new Map<string, CalendarVisit[]>()
  for (const visit of shown) byDay.set(visit.on, [...(byDay.get(visit.on) ?? []), visit])
  const inMonth = shown.filter((visit) => visit.on.startsWith(month))
  const overdue = inMonth.filter((visit) => markOfVisit(visit) === 'overdue').length
  const paused = inMonth.filter((visit) => markOfVisit(visit) === 'paused').length
  const weeks = Array.from({ length: days.length / 7 }, (_, index) => days.slice(index * 7, index * 7 + 7))

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-[var(--row-pad-x)] py-2.5">
        <div className="flex min-w-0 items-baseline gap-3">
          <h2 className="text-[15px] font-semibold text-ink-900">{monthTitle(month)}</h2>
          {data ? (
            <span className="text-xs text-ink-500">
              {[
                plural(inMonth.length, 'visit'),
                overdue ? `${overdue} overdue` : null,
                paused ? `${paused} paused` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            aria-label="Previous month"
            icon={<Icon name="back" className="size-4" />}
            onClick={() => onMonth(addCalendarMonths(`${month}-01`, -1).slice(0, 7))}
          />
          <Button size="sm" disabled={month === now.slice(0, 7)} onClick={() => onMonth(now.slice(0, 7))}>
            This month
          </Button>
          <Button
            size="sm"
            aria-label="Next month"
            icon={<Icon name="forward" className="size-4" />}
            onClick={() => onMonth(addCalendarMonths(`${month}-01`, 1).slice(0, 7))}
          />
        </div>
      </div>

      {error ? (
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      ) : isPending ? (
        <SkeletonRows />
      ) : (
        <div className={cn('transition-opacity', isPlaceholderData && 'opacity-60')}>
          <div
            role="grid"
            aria-label={`Agreement visits, ${monthTitle(month)}`}
            className="hidden grid-cols-7 desk:grid"
          >
            <div role="row" className="contents">
              {WEEKDAYS.map((name) => (
                <span
                  key={name}
                  role="columnheader"
                  className="border-b border-ink-100 py-1.5 text-center text-[10px] font-bold tracking-[0.06em] text-ink-400 uppercase"
                >
                  {name}
                </span>
              ))}
            </div>
            {weeks.map((week) => (
              <div key={week[0]} role="row" className="contents">
                {week.map((day) => {
                  const visits = byDay.get(day) ?? []
                  const outside = !day.startsWith(month)
                  const isToday = day === now
                  const extra = visits.length - PER_DAY
                  return (
                    <div
                      key={day}
                      role="gridcell"
                      aria-label={longDay(day)}
                      aria-selected={day === selected}
                      className={cn(
                        'flex min-h-28 min-w-0 flex-col gap-1 border-b border-l border-ink-100 p-1.5 first:border-l-0',
                        outside && 'bg-ink-50/70',
                        day === selected && 'bg-brand-50/60 ring-1 ring-brand-300 ring-inset',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => setPicked(day === selected ? null : day)}
                        aria-label={`${longDay(day)}, ${plural(visits.length, 'visit')}`}
                        className={cn(
                          'grid size-6 place-items-center rounded-full text-xs tabular-nums hover:bg-ink-100',
                          isToday ? 'bg-red-600 font-bold text-white hover:bg-red-700' : outside ? 'text-ink-400' : 'font-medium text-ink-700',
                        )}
                      >
                        {Number(day.slice(8))}
                      </button>
                      {visits.slice(0, extra > 0 ? PER_DAY - 1 : PER_DAY).map((visit) => (
                        <VisitChip key={visit.key} visit={visit} />
                      ))}
                      {extra > 0 ? (
                        <button
                          type="button"
                          onClick={() => setPicked(day)}
                          className="rounded px-1.5 text-left text-[11px] font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900"
                        >
                          {`+${extra + 1} more`}
                        </button>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>

          {selected ? (
            <section aria-label={`Picked day, ${longDay(selected)}`} className="hidden border-t border-ink-200 desk:block">
              <div className="flex items-center justify-between gap-2 px-[var(--row-pad-x)] pt-3 pb-1">
                <h3 className="text-[13px] font-semibold text-ink-900">
                  {longDay(selected)}
                  <span className="ml-2 font-normal text-ink-500">{plural(byDay.get(selected)?.length ?? 0, 'visit')}</span>
                </h3>
                <Button size="sm" variant="ghost" aria-label="Close the day" icon={<Icon name="close" className="size-4" />} onClick={() => setPicked(null)} />
              </div>
              <VisitList visits={byDay.get(selected) ?? []} />
            </section>
          ) : null}

          <div className="desk:hidden">
            {inMonth.length === 0 ? (
              <EmptyState title="No visits this month" description="Try another month or clear a filter." />
            ) : (
              [...byDay.entries()]
                .filter(([day]) => day.startsWith(month))
                .map(([day, visits]) => (
                  <section key={day} aria-label={longDay(day)} className="border-b border-ink-100 last:border-b-0">
                    <h3
                      className={cn(
                        'px-[var(--row-pad-x)] pt-3 pb-1 text-xs font-semibold',
                        day === now ? 'text-red-700' : 'text-ink-700',
                      )}
                    >
                      {longDay(day)}
                      {day === now ? ' · Today' : ''}
                    </h3>
                    <VisitList visits={visits} />
                  </section>
                ))
            )}
          </div>

          <SlotKey
            look="dots"
            marks={['done', 'booked', 'planned', 'overdue', 'paused']}
            className="border-t border-ink-100 px-[var(--row-pad-x)] py-2.5"
          />
        </div>
      )}
    </Card>
  )
}

const CHIP: Record<Mark, string> = {
  done: 'bg-green-50 text-green-900 hover:bg-green-100',
  current: 'bg-blue-50 text-blue-900 hover:bg-blue-100',
  booked: 'bg-blue-50 text-blue-900 hover:bg-blue-100',
  planned: 'bg-white text-ink-800 ring-1 ring-ink-200 ring-inset hover:bg-ink-50',
  overdue: 'bg-amber-50 text-amber-900 hover:bg-amber-100',
  skipped: 'bg-ink-50 text-ink-500 hover:bg-ink-100',
  paused: 'bg-ink-50 text-ink-500 hover:bg-ink-100',
}

function VisitChip({ visit }: { visit: CalendarVisit }) {
  const mark = markOfVisit(visit)
  return (
    <VisitLink
      visit={visit}
      className={cn('flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[11.5px] leading-tight', CHIP[mark])}
    >
      <span aria-hidden="true" className="grid w-2.5 shrink-0 place-items-center">
        <SlotDot mark={mark} small look="dots" />
      </span>
      <span className="min-w-0 truncate">
        <span className="font-medium">{visitSite(visit)}</span>
        <span className="opacity-70">{` · ${visit.services.join(', ') || 'No service'}`}</span>
      </span>
    </VisitLink>
  )
}

/** A day's visits, one line each: what, where, whose agreement and where it is up to. */
function VisitList({ visits }: { visits: readonly CalendarVisit[] }) {
  if (visits.length === 0) return <p className="px-[var(--row-pad-x)] pb-3 text-xs text-ink-500">Nothing on this day.</p>
  return (
    <ul className="divide-y divide-ink-100 pb-1">
      {visits.map((visit) => {
        const mark = markOfVisit(visit)
        return (
          <li key={visit.key}>
            <VisitLink
              visit={visit}
              className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 px-[var(--row-pad-x)] py-[var(--row-pad-y)] hover:bg-ink-50 desk:grid-cols-[auto_minmax(0,1.3fr)_minmax(0,1fr)_auto]"
            >
              <span aria-hidden="true" className="grid w-4 place-items-center">
                <SlotDot mark={mark} look="dots" />
              </span>
              <span className="min-w-0 truncate text-[13px]">
                <span className="font-semibold text-ink-900">{visit.services.join(', ') || 'No service'}</span>
                <span className="text-ink-500">{` · ${visitSite(visit)}`}</span>
              </span>
              <span className="col-start-2 min-w-0 truncate text-xs text-ink-500 desk:col-start-auto">
                <span className="font-mono text-ink-400">{`AG-${visit.agreement.number}`}</span>
                {` ${visit.agreement.name} · ${visit.client.name}`}
              </span>
              <span className="col-start-3 row-start-1 text-right text-xs whitespace-nowrap text-ink-600 tabular-nums desk:col-start-auto">
                {visit.job ? `${formatJobNumber(visit.job)} · ` : ''}
                {MARK_LABEL[mark]}
                {visit.slot.tentative ? ' (may move)' : ''}
                {visit.priceIncTax ? <span className="ml-2 text-ink-400">{money(visit.priceIncTax)}</span> : null}
              </span>
            </VisitLink>
          </li>
        )
      })}
    </ul>
  )
}
